import React, { Component } from 'react';
import { motion } from 'framer-motion';
import { Check, Star, Zap, Rocket, Crown, Clock } from 'lucide-react';
interface Plan {
  name: string;
  subtitle: string;
  price: number;
  originalPrice?: number;
  currency: string;
  badge?: string;
  badgeColor?: string;
  description: string;
  features: string[];
  cta: string;
  highlighted: boolean;
  promoLabel?: string;
  whatsappMessage: string;
}
const WA_BASE = 'https://wa.me/50685316690';
const plans: Plan[] = [{
  name: 'Mis Primeros Pasos',
  subtitle: 'Ideal para emprendedores que inician',
  price: 250,
  currency: 'USD',
  description: 'Tu primera landing page profesional para comenzar a captar clientes y validar tu idea de negocio.',
  features: ['Diseño profesional de 1 página', 'Copy básico persuasivo', 'Formulario de contacto', 'Botón de WhatsApp', 'Diseño responsive (móvil y desktop)', 'Entrega en 7 días hábiles', '1 ronda de revisiones', 'Hosting incluido (6 meses)'],
  cta: 'Comenzar Ahora',
  highlighted: false,
  whatsappMessage: 'Hola, estoy interesado en adquirir el plan *Mis Primeros Pasos* ($250 USD). Me gustaría recibir más información.'
}, {
  name: 'Despegue Digital',
  subtitle: 'Para negocios listos para crecer',
  price: 500,
  currency: 'USD',
  badge: 'POPULAR',
  badgeColor: 'from-cyan-400 to-blue-500',
  description: 'Una landing page optimizada para conversión con integraciones clave para escalar tus ventas.',
  features: ['Diseño premium personalizado', 'Copy persuasivo profesional', 'Dominio propio incluido (1 año)', 'Integración con Meta Ads', 'Botón de WhatsApp inteligente', 'Formulario avanzado con CRM', 'Optimización SEO básica', 'Diseño responsive optimizado', 'Entrega en 5 días hábiles', '2 rondas de revisiones', 'Soporte por 15 días'],
  cta: 'Quiero Despegar',
  highlighted: false,
  whatsappMessage: 'Hola, estoy interesado en adquirir el plan *Despegue Digital* ($500 USD). Me gustaría recibir más información.'
}, {
  name: 'Dominio Total',
  subtitle: 'La experiencia completa A-Z',
  price: 800,
  originalPrice: 1200,
  currency: 'USD',
  badge: 'MEJOR VALOR',
  badgeColor: 'from-yellow-400 to-orange-500',
  promoLabel: 'Por tiempo limitado',
  description: 'El paquete definitivo: diseño ultra creativo, estrategia AI, integraciones avanzadas y soporte continuo.',
  features: ['Diseño ultra creativo personalizado', 'Copy premium con estrategia AI', 'Dominio y hosting incluido (1 año)', 'Integración completa con Meta Ads', 'Botón de WhatsApp inteligente', 'Conexión a CRM y email marketing', 'Pruebas A/B para optimización', 'Optimización SEO avanzada', 'Analytics y tracking completo', 'Entrega en 5 días hábiles', '3 rondas de revisiones', 'Soporte continuo por 30 días'],
  cta: 'Dominar Mi Mercado',
  highlighted: true,
  whatsappMessage: 'Hola, estoy interesado en adquirir el plan *Dominio Total* ($800 USD - oferta por tiempo limitado). Me gustaría recibir más información.'
}];
const cardIcons = [Zap, Rocket, Crown];
export function PricingSection() {
  return <section id="precios" className="py-24 bg-[#0a0a1a] relative overflow-hidden" data-id="element-148">
      {/* Background Glows */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-600/15 rounded-full blur-[120px]" data-id="element-149" />
      <div className="absolute top-1/4 right-0 w-[400px] h-[400px] bg-pink-600/10 rounded-full blur-[100px]" data-id="element-150" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-151">
        <div className="text-center mb-16" data-id="element-152">
          <h2 className="text-3xl md:text-5xl font-bold font-orbitron mb-4" data-id="element-153">
            Tu Inversión en el{' '}
            <span className="text-gradient-cosmic" data-id="element-154">Éxito Digital</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg" data-id="element-155">
            Elige el plan perfecto para tu negocio. Cada paquete está diseñado
            para llevarte al siguiente nivel.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch" data-id="element-156">
          {plans.map((plan, index) => {
          const Icon = cardIcons[index];
          return <motion.div key={plan.name} initial={{
            opacity: 0,
            y: 40
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.15
          }} className={`relative flex flex-col ${plan.highlighted ? 'lg:-mt-4 lg:mb-[-16px]' : ''}`} data-id="element-157">
                {/* Glow border for highlighted */}
                {plan.highlighted && <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 via-pink-600 to-yellow-500 rounded-3xl blur opacity-60 animate-pulse-glow" data-id="element-158" />}

                <div className={`relative flex flex-col flex-1 rounded-3xl p-8 border transition-all duration-300 ${plan.highlighted ? 'bg-[#050510] border-white/20 shadow-2xl' : 'bg-[#050510]/80 border-white/10 hover:border-purple-500/30'}`} data-id="element-159">
                  {/* Badge */}
                  {plan.badge && <div className="absolute top-0 right-0 p-4" data-id="element-160">
                      <div className={`bg-gradient-to-r ${plan.badgeColor} text-black text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1`} data-id="element-161">
                        <Star className="w-3 h-3 fill-black" data-id="element-162" />
                        {plan.badge}
                      </div>
                    </div>}

                  {/* Icon & Name */}
                  <div className="mb-6" data-id="element-163">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${plan.highlighted ? 'bg-gradient-to-br from-purple-500/20 to-pink-500/20' : 'bg-white/5'}`} data-id="element-164">
                      <Icon className={`w-6 h-6 ${index === 0 ? 'text-cyan-400' : index === 1 ? 'text-purple-400' : 'text-yellow-400'}`} data-id="element-165" />
                    </div>
                    <h3 className="text-2xl font-bold text-white font-orbitron mb-1" data-id="element-166">
                      {plan.name}
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-167">{plan.subtitle}</p>
                  </div>

                  {/* Price */}
                  <div className="mb-6" data-id="element-168">
                    {plan.originalPrice && <div className="flex items-center gap-3 mb-1" data-id="element-169">
                        <span className="text-2xl text-gray-600 line-through font-orbitron" data-id="element-170">
                          ${plan.originalPrice.toLocaleString()}
                        </span>
                        {plan.promoLabel && <span className="inline-flex items-center gap-1 text-xs font-bold text-red-400 bg-red-400/10 px-2 py-1 rounded-full" data-id="element-171">
                            <Clock className="w-3 h-3" data-id="element-172" />
                            {plan.promoLabel}
                          </span>}
                      </div>}
                    <div className="flex items-baseline gap-2" data-id="element-173">
                      <span className={`text-4xl md:text-5xl font-bold font-orbitron ${plan.highlighted ? 'text-gradient-cosmic' : 'text-white'}`} data-id="element-174">
                        ${plan.price}
                      </span>
                      <span className="text-lg text-gray-400" data-id="element-175">
                        {plan.currency}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-176">Pago único</p>
                  </div>

                  {/* Description */}
                  <p className="text-gray-400 text-sm leading-relaxed mb-6 border-b border-white/5 pb-6" data-id="element-177">
                    {plan.description}
                  </p>

                  {/* Features */}
                  <div className="space-y-3 mb-8 flex-1" data-id="element-178">
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4" data-id="element-179">
                      Qué incluye:
                    </p>
                    {plan.features.map((feature, i) => <div key={i} className="flex items-start gap-3" data-id="element-180">
                        <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${plan.highlighted ? 'bg-green-500/20' : 'bg-white/5'}`} data-id="element-181">
                          <Check className={`w-3 h-3 ${plan.highlighted ? 'text-green-400' : 'text-gray-500'}`} data-id="element-182" />
                        </div>
                        <span className="text-sm text-gray-300" data-id="element-183">{feature}</span>
                      </div>)}
                  </div>

                  {/* CTA */}
                  <div className="mt-auto space-y-3" data-id="element-184">
                    <a href={`${WA_BASE}?text=${encodeURIComponent(plan.whatsappMessage)}`} target="_blank" rel="noopener noreferrer" className={`block w-full py-4 rounded-xl font-bold text-center text-lg transition-all duration-300 transform hover:scale-[1.02] ${plan.highlighted ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:shadow-[0_0_30px_rgba(217,70,239,0.5)]' : 'bg-white/5 text-white border border-white/10 hover:bg-white/10 hover:border-purple-500/30'}`} data-id="element-185">
                      {plan.cta}
                    </a>
                    <p className="text-center text-xs text-gray-600" data-id="element-186">
                      {plan.highlighted ? 'Garantía de satisfacción • Pago seguro' : 'Sin compromiso • Pago seguro'}
                    </p>
                  </div>
                </div>
              </motion.div>;
        })}
        </div>

        {/* Trust note */}
        <motion.p initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.6
      }} className="text-center text-gray-600 text-sm mt-12" data-id="element-187">
          Todos los planes incluyen consulta gratuita inicial • Aceptamos
          transferencia, tarjeta y PayPal
        </motion.p>
      </div>
    </section>;
}